import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { ArrowLeft, Save, Upload, Check } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';

const TalentProfileEdit = () => {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    headline: '',
    bio: '',
    function: '',
    experience_years: '',
    linkedin_url: '',
    portfolio_url: '',
    resume_url: '',
    availability: '',
  });

  usePageMeta({
    title: 'Edit Profile | Talent Collective',
    description: 'Update your professional information and preferences.'
  });

  // Map experience years to the correct select value
  const getExperienceSelectValue = (years) => {
    if (!years) return '';
    const num = parseInt(years);
    if (num <= 5) return '3';
    if (num <= 12) return '8';
    if (num <= 20) return '15';
    return '25';
  };

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        headline: profile.headline || '',
        bio: profile.bio || '',
        function: profile.function || '',
        experience_years: getExperienceSelectValue(profile.experience_years),
        linkedin_url: profile.linkedin_url || '',
        portfolio_url: profile.portfolio_url || '',
        resume_url: profile.resume_url || '',
        availability: profile.availability?.toString() || '',
      });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/talent/profile`, {
        ...formData,
        experience_years: parseInt(formData.experience_years),
        availability: parseInt(formData.availability)
      });
      await refreshProfile();
      toast.success('Profile updated successfully.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, resume_url: response.data.url });
      toast.success('Resume uploaded successfully.');
    } catch {
      toast.error('Failed to upload resume.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-edit-page">
      <Header />

      <div className="container-custom py-12 max-w-3xl">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="heading-2 mb-2">Edit Profile</h1>
          <p className="text-slate-600">Update your professional information.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="p-8 mb-6" data-testid="basic-info-card">
            <h2 className="heading-4 mb-6">Basic Information</h2>
            
            <div className="grid gap-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Function *</Label>
                  <Select value={formData.function} onValueChange={(v) => setFormData({ ...formData, function: v })}>
                    <SelectTrigger data-testid="function-select">
                      <SelectValue placeholder="Select function" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Product Management</SelectItem>
                      <SelectItem value="design">Design</SelectItem>
                      <SelectItem value="engineering">Engineering</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="headline">Headline</Label>
                <Input
                  id="headline"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  placeholder="e.g., Senior Product Manager | Growth & Strategy"
                  data-testid="headline-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell organizations about your background and what drives you..."
                  className="min-h-[120px]"
                  data-testid="bio-input"
                />
              </div>
            </div>
          </Card>

          <Card className="p-8 mb-6" data-testid="experience-card">
            <h2 className="heading-4 mb-6">Experience & Availability</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Years of Experience *</Label>
                <Select value={formData.experience_years} onValueChange={(v) => setFormData({ ...formData, experience_years: v })}>
                  <SelectTrigger data-testid="experience-select">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">0 - 5 years</SelectItem>
                    <SelectItem value="8">5 - 12 years</SelectItem>
                    <SelectItem value="15">12 - 20 years</SelectItem>
                    <SelectItem value="25">20+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Availability (hrs/week) *</Label>
                <Select value={formData.availability} onValueChange={(v) => setFormData({ ...formData, availability: v })}>
                  <SelectTrigger data-testid="availability-select">
                    <SelectValue placeholder="Select hours" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 25, 30, 40].map((h) => (
                      <SelectItem key={h} value={h.toString()}>{h} hours</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card className="p-8 mb-6" data-testid="links-card">
            <h2 className="heading-4 mb-6">Links & Documents</h2>
            
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="linkedin">LinkedIn URL *</Label>
                <Input
                  id="linkedin"
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  required
                  data-testid="linkedin-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="portfolio">Portfolio URL</Label>
                <Input
                  id="portfolio"
                  type="url"
                  value={formData.portfolio_url}
                  onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                  data-testid="portfolio-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Resume (PDF)</Label>
                <div className="flex items-center gap-4">
                  {formData.resume_url ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-600">
                      <Check className="h-4 w-4" />
                      Resume uploaded.
                    </div>
                  ) : (
                    <span className="text-sm text-slate-500">No resume uploaded.</span>
                  )}
                  <label className="cursor-pointer">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        {formData.resume_url ? 'Replace' : 'Upload'}
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={handleFileUpload}
                      data-testid="resume-upload"
                    />
                  </label>
                </div>
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>
              Cancel
            </Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="save-profile-btn">
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default TalentProfileEdit;
